import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { isNumber } from 'radash';
import { CSSProperties } from 'react';
import { LayoutEnum } from '@goldcast/api/content';
import { DEFAULT_LAYOUT_TYPE, DEFAULT_FIT_TYPE, DEFAULT_HIDE_BORDERS, DEFAULT_SIZE_TYPE } from '../../../constants';
import { LayoutChange, VideoCropPositionStyle } from '../../../types/Segment/LayoutChange';
import { PlayRanges } from '../../../types/Segment/PlayRange';
import { Scene, VideoCropPosition } from '../../../types/Scene';
import { getSpeakerOverlaps } from './speakerOverlaps';
import { getAdjustedFrame, getFlattenedDeletes, getAvailableRanges } from './utils';
import { getScreenshareCropPositionMap } from './scenes';
import { VideoElement } from '../../../types/Element/VideoElement';
import { getActiveSpeakersWithinTimeRange } from '../../activeSpeaker';
import {
  doesRecordingHaveSpeaker,
  getRecordingSpeakerKey,
  getVideoDetailsForBroadcast,
  isRecordingScreenshare
} from '../../../stores/videoSpeakerDetailsStore';

import { BaseConfig } from '../../speakerLayoutGenerator/types';
import { getSpeakerDetailsForBroadcast } from '../../../stores/speakerDetailsStore';
import { SegmentType } from '../../../types/Segment/Type';
import { UPLOAD_SCREENSHARE_ID } from './constants';
import { CompositeSegment } from '../../../types/Segment/CompositeSegment';
import {
  getClipBroadcastId,
  isClipAudiogram,
  isClipFromUploads,
  isClipLayoutDefault,
  isCustomUploadClip
} from '@/libs/clipUtils';
import { Clip, FitType, LayoutType } from '@/domains/asset';
import { isLayoutAudiogram, isLayoutDefault, isSpeakerLayout } from '@/libs/layout';
import {
  doesClipWorkOnAlignedStreams,
  isLiveEventRemotionWithPreviewAndAlignedStream,
  isScreenShareEnabledForCustomUploadClips
} from '@/stores/clip';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';
import { layoutConfigMap } from '@/App/remotion/components/SegmentComponent/layoutConfigMap';
import { getCropStyles, getFitCropStyles } from '@/App/remotion/components/SegmentComponent/cropUtils';
import { getFinalConfigForLayout } from '@/App/remotion/components/SegmentComponent/layoutUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { getAspectRatio } from '@/Pages/Clip/ClipPlayer/ClipTimeline/ClipTimelineUtils';
import { FacePositions } from '@/stores/speakersAnalysis/speakersAnalysisTypes';
import { roundToNDecimalPlaces } from '@/libs/utils';
import { getSegmentAtCurrentFrame } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { isApproxWindowSubset } from '@/libs/compare';

const removeScreenshareFromVideoIds = ({ clip, videoIds }: { clip: Clip; videoIds: string[] }) => {
  return videoIds.filter(id => !isRecordingScreenshare({ broadcastId: getClipBroadcastId(clip)!, recordingId: id }));
};

const removeScreenshareWithoutSpeaker = ({ clip, videoIds }: { clip: Clip; videoIds: string[] }) => {
  return videoIds.filter(recordingId => {
    if (!isRecordingScreenshare({ broadcastId: getClipBroadcastId(clip)!, recordingId: recordingId })) {
      return true;
    }
    // filter in screenshare if speaker exists
    return doesRecordingHaveSpeaker({ broadcastId: getClipBroadcastId(clip)!, recordingId: recordingId });
  });
};

const getCropPositionsForUploads = ({
  clip,
  overlap,
  fps,
  finalLayout,
  fitMode,
  hideBorders,
  finalVideoIds,
  videos,
  hasVisibleVideoIds
}: {
  clip: Clip;
  overlap: { fromFrame: number; toFrame: number; hasScreenshare: boolean };
  fps: number;
  finalLayout: Exclude<LayoutType, 'SPEAKER'>;
  fitMode: FitType;
  hideBorders: boolean;
  finalVideoIds: string[];
  videos: VideoElement[];
  hasVisibleVideoIds: boolean;
}):
  | {
      videoIds: string[];
      layoutForOverlap: Exclude<LayoutType, 'SPEAKER'>;
      videoCropPositionMap: Record<string, VideoCropPosition> | undefined;
      faceCoordinatesMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const currentTime = roundToNDecimalPlaces(overlap.fromFrame / fps, 4);
  const containerConfigsForAllNumberOfVideos =
    layoutConfigMap[clip.asset_metadata.size][finalLayout][fitMode][getSpeakerLayoutBorderKey(hideBorders)];

  const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];

  if (!speakersAnalysis || !speakersAnalysis.time_analysis || speakersAnalysis.time_analysis.length === 0) {
    return undefined;
  }

  // Having to do Math.max because of JS precision issues
  // Assumption is that there would always be a record in speakersAnalysis.times as we have a time_analysis check above
  // It is mainly harmful at the start only
  const timeIndex = Math.max(
    speakersAnalysis.times.findLastIndex(t => t <= currentTime),
    0
  );

  const timeAnalysisPoint = speakersAnalysis.time_analysis[timeIndex];

  const facePositions = timeAnalysisPoint.face_positions;

  let { videoCropPositionMap: videoCropPositionMapForUploads } =
    getVideoCropPositionMapForUploads({
      facePositions: structuredClone(facePositions),
      speakerIdToFaceMap: speakersAnalysis.speaker_mapping,
      selectedVisibleVideoIds: finalVideoIds,
      containerConfigsForAllNumberOfVideos,
      broadcastId: getClipBroadcastId(clip)!
    }) || {};

  const shouldIncludeScreenshare =
    isScreenShareEnabledForCustomUploadClips(clip) &&
    speakersAnalysis?.screenshare_bounds &&
    speakersAnalysis.screenshare_bounds.length > 0 &&
    finalVideoIds.includes(UPLOAD_SCREENSHARE_ID);

  if (shouldIncludeScreenshare) {
    const currentFrame = overlap.fromFrame;
    const screenshareCrops = getScreenshareCropPositionMap({
      frame: currentFrame,
      fps,
      speakersAnalysis
    });
    if (screenshareCrops) {
      videoCropPositionMapForUploads = { ...screenshareCrops, ...videoCropPositionMapForUploads };
    }
  }

  const { faceBoundingBoxMap } =
    getAllVideoCropPositionMapForUploads({
      facePositions: structuredClone(facePositions),
      speakerIdToFaceMap: speakersAnalysis.speaker_mapping,
      broadcastId: getClipBroadcastId(clip)!
    }) || {};

  const faceCoordinatesMap: Record<string, VideoCropPosition> = {};
  faceBoundingBoxMap &&
    Object.keys(faceBoundingBoxMap).forEach(videoId => {
      faceCoordinatesMap[videoId] = faceBoundingBoxMap?.[videoId];
    });

  const cropPositionVideoIds = Object.keys(videoCropPositionMapForUploads ?? {});

  const originalVideo = videos.find(v => v.isOriginalVideo);

  // fallback to first video if no crop positions are available
  let videoIds = cropPositionVideoIds;
  if (facePositions.length === 0 && originalVideo) {
    videoIds = cropPositionVideoIds.length > 0 ? cropPositionVideoIds : [originalVideo.id];
  }
  let layoutForOverlap = finalLayout;

  // fallback to original video if no crop positions are available and there are visible video ids
  if (!videoCropPositionMapForUploads && hasVisibleVideoIds && originalVideo) {
    videoIds = [originalVideo.id];
    layoutForOverlap = 'GRID';
  }

  if (videoIds.length <= 1) {
    layoutForOverlap = 'GRID';
  }

  return {
    videoIds,
    layoutForOverlap,
    videoCropPositionMap: videoCropPositionMapForUploads,
    faceCoordinatesMap
  };
};

export const getAllVideoCropPositionMapForUploads = ({
  facePositions,
  speakerIdToFaceMap,
  broadcastId
}: {
  facePositions: FacePositions[];
  speakerIdToFaceMap: Record<string, number[]>;
  broadcastId: string;
}):
  | {
      videoCropPositionMap: Record<string, VideoCropPosition>;
      faceBoundingBoxMap: Record<string, VideoCropPosition>;
      personBoundingBoxMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const speakerDetails = getSpeakerDetailsForBroadcast(broadcastId);
  const videoIdToSpeakerDetails = getVideoDetailsForBroadcast({ broadcastId });

  const speakerKeyToSpeakerIdMap = Object.keys(speakerDetails).reduce(
    (acc, speakerKey) => {
      acc[speakerKey] = speakerDetails[speakerKey].id;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToSpeakerKeyMap = Object.keys(videoIdToSpeakerDetails).reduce(
    (acc, videoId) => {
      acc[videoId] = videoIdToSpeakerDetails[videoId].speakerKey;
      return acc;
    },
    {} as Record<string, string>
  );

  // Create a map of video IDs to expanded crop positions
  // For each selected video, find its face position and calculate crop
  const videoCropPositionMap: Record<string, VideoCropPosition> = {};
  const videoPersonBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoFaceBoundingBoxMap: Record<string, VideoCropPosition> = {};

  // Map each selected video to a container position based on its order in selectedVisibleVideoIds
  Object.keys(videoIdToSpeakerKeyMap).forEach(videoId => {
    const speakerKey = videoIdToSpeakerKeyMap[videoId];
    if (!speakerKey) {
      return;
    }

    const speakerId = speakerKeyToSpeakerIdMap[speakerKey];
    if (!speakerId) {
      return;
    }

    const faceIds = speakerIdToFaceMap[speakerId] || [];

    // Find the face position for this speaker's face
    const facePosition = facePositions.find(fp => faceIds.includes(fp.face_id));
    if (!facePosition) {
      return;
    }

    // Get face and person bounding boxes
    const faceBoundingBox = facePosition.crop_position_face || facePosition.crop_position;
    const personBoundingBox = facePosition.crop_position_person;

    if (!faceBoundingBox) {
      return;
    }

    // Choose which bounding box to use
    let boundingBoxToExpand: VideoCropPosition;

    if (!personBoundingBox) {
      // No person box, use face
      boundingBoxToExpand = faceBoundingBox;
    } else {
      // Have person box, use it for better framing
      videoPersonBoundingBoxMap[videoId] = personBoundingBox;
      boundingBoxToExpand = personBoundingBox;
    }
    videoFaceBoundingBoxMap[videoId] = faceBoundingBox;
    videoPersonBoundingBoxMap[videoId] = personBoundingBox || faceBoundingBox;
    videoCropPositionMap[videoId] = boundingBoxToExpand;
  });

  return Object.keys(videoCropPositionMap).length > 0
    ? {
        videoCropPositionMap,
        faceBoundingBoxMap: videoFaceBoundingBoxMap,
        personBoundingBoxMap: videoPersonBoundingBoxMap
      }
    : undefined;
};

export const getVideoCropPositionMapForUploads = ({
  facePositions,
  speakerIdToFaceMap,
  selectedVisibleVideoIds,
  containerConfigsForAllNumberOfVideos,
  broadcastId
}: {
  facePositions: FacePositions[];
  speakerIdToFaceMap: Record<string, number[]>;
  selectedVisibleVideoIds: string[];
  containerConfigsForAllNumberOfVideos: {
    [numberOfVideos: number]: BaseConfig[];
  };
  broadcastId: string;
}):
  | {
      videoCropPositionMap: Record<string, VideoCropPosition>;
      faceBoundingBoxMap: Record<string, VideoCropPosition>;
      personBoundingBoxMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const speakerDetails = getSpeakerDetailsForBroadcast(broadcastId);
  const videoIdToSpeakerDetails = getVideoDetailsForBroadcast({ broadcastId });

  const speakerKeyToSpeakerIdMap = Object.keys(speakerDetails).reduce(
    (acc, speakerKey) => {
      acc[speakerKey] = speakerDetails[speakerKey].id;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToSpeakerKeyMap = Object.keys(videoIdToSpeakerDetails).reduce(
    (acc, videoId) => {
      acc[videoId] = videoIdToSpeakerDetails[videoId].speakerKey;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToFaceMap = Object.keys(videoIdToSpeakerKeyMap).reduce(
    (acc, videoId) => {
      const faceIdForVideoId = speakerIdToFaceMap?.[speakerKeyToSpeakerIdMap?.[videoIdToSpeakerKeyMap?.[videoId]]]?.[0];
      if (isNumber(faceIdForVideoId)) {
        acc[videoId] = faceIdForVideoId;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  const selectedFaces = selectedVisibleVideoIds
    .map(videoId => videoIdToFaceMap[videoId])
    .filter(a => a !== undefined && a !== null);

  const filteredFacePositions = facePositions.filter(fp => selectedFaces.includes(fp.face_id));

  if (filteredFacePositions.length === 0) {
    return undefined;
  }

  // Get container configurations based on the number of SELECTED videos, not detected faces
  // This ensures consistent positioning even when face detection count varies
  const containerConfigForGivenVideos = containerConfigsForAllNumberOfVideos[selectedVisibleVideoIds.length];

  if (!containerConfigForGivenVideos) {
    return undefined;
  }

  // Create a map of video IDs to expanded crop positions
  // For each selected video, find its face position and calculate crop
  const videoCropPositionMap: Record<string, VideoCropPosition> = {};
  const videoPersonBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoFaceBoundingBoxMap: Record<string, VideoCropPosition> = {};

  // Map each selected video to a container position based on its order in selectedVisibleVideoIds
  selectedVisibleVideoIds.forEach((videoId, videoIndex) => {
    const speakerKey = videoIdToSpeakerKeyMap[videoId];
    if (!speakerKey) {
      return;
    }

    const speakerId = speakerKeyToSpeakerIdMap[speakerKey];
    if (!speakerId) {
      return;
    }

    const faceIds = speakerIdToFaceMap[speakerId];
    if (!faceIds || faceIds.length === 0) {
      // fallback to full video if no associated face ids are found
      videoCropPositionMap[videoId] = {
        top_left: { x: 0, y: 0 },
        bottom_right: { x: 1, y: 1 }
      };
      return;
    }

    // Find the face position for this speaker's face
    const facePosition = filteredFacePositions.find(fp => faceIds.includes(fp.face_id));
    if (!facePosition) {
      return;
    }

    const containerConfig = containerConfigForGivenVideos[videoIndex];
    if (!containerConfig?.containerStyle) {
      return;
    }

    // Get face and person bounding boxes
    const faceBoundingBox = facePosition.crop_position_face || facePosition.crop_position;
    const personBoundingBox = facePosition.crop_position_person;

    if (!faceBoundingBox) {
      return;
    }

    // Choose which bounding box to use
    let boundingBoxToExpand: VideoCropPosition;

    if (!personBoundingBox) {
      // No person box, use face
      boundingBoxToExpand = faceBoundingBox;
    } else {
      // Have person box, use it for better framing
      videoPersonBoundingBoxMap[videoId] = personBoundingBox;
      boundingBoxToExpand = personBoundingBox;
    }
    videoFaceBoundingBoxMap[videoId] = faceBoundingBox;
    videoPersonBoundingBoxMap[videoId] = personBoundingBox || faceBoundingBox;
    videoCropPositionMap[videoId] = boundingBoxToExpand;
  });

  return Object.keys(videoCropPositionMap).length > 0
    ? {
        videoCropPositionMap,
        faceBoundingBoxMap: videoFaceBoundingBoxMap,
        personBoundingBoxMap: videoPersonBoundingBoxMap
      }
    : undefined;
};

export const getMutedVideoIds = ({
  clip,
  videosInLayout,
  videos,
  isCreatedFromOriginalLayout
}: {
  clip: Clip;
  videosInLayout: string[];
  videos: VideoElement[];
  isCreatedFromOriginalLayout: boolean;
}) => {
  // for uploads, in any case, we want have only 1 video playing at a time
  if (isCustomUploadClip({ clip })) {
    const videoIdToPlay = videosInLayout[0];
    if (videoIdToPlay) {
      return videos.filter(v => v.id !== videoIdToPlay).map(v => v.id);
    }
    return videos.map(v => v.id);
  }

  let mutedVideosId: string[] = [];
  if (!doesClipWorkOnAlignedStreams(clip)) {
    // For non-aligned stream videos, there is only one real video, and playing one of them is enough
    // For both audiogram and default layout atmost one video will be part of layout (So no overlapping on seeking)
    // https://goldcast.atlassian.net/browse/CR-6945
    // https://goldcast.atlassian.net/browse/CR-7367
    mutedVideosId = videos.filter(v => !videosInLayout.includes(v.id)).map(v => v.id);
  }
  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip)) {
    if (isCreatedFromOriginalLayout) {
      // mute all individual videos in default layout
      mutedVideosId = videos.filter(v => !v.isOriginalVideo).map(v => v.id);
    } else {
      // mute original video in speaker layouts
      mutedVideosId = videos.filter(v => v.isOriginalVideo).map(v => v.id);
    }
  }

  return mutedVideosId;
};

// If making any change to this function, make sure to update the getLayoutChangesAndPlayRangesWithSplits function
export const getLayoutChangesAndPlayRanges = ({
  clip,
  fps,
  layout = DEFAULT_LAYOUT_TYPE,
  size = DEFAULT_SIZE_TYPE,
  fitMode = DEFAULT_FIT_TYPE,
  hideBorders = DEFAULT_HIDE_BORDERS,
  scenes,
  videos
}: Pick<LayoutChange, 'layout' | 'size' | 'fitMode' | 'hideBorders'> & {
  clip: Clip;
  fps: number;
  scenes: Scene[];
  videos: VideoElement[];
}) => {
  // get exact points across the entire clip with videos within those points, including screenshare
  const speakerOverlaps = getSpeakerOverlaps({ clip, fps, scenes });
  // clips can start from non-zero offset
  let start = clip.asset_metadata.is_full_length ? 0 : clip.asset_metadata.start * fps;
  const end = clip.asset_metadata.end * fps;

  let flattenedDeletes = getFlattenedDeletes({ clipDeletes: clip.asset_metadata.deletes || {}, fps });
  if (flattenedDeletes.length > 0 && flattenedDeletes[0].fromFrame === 0) {
    start = flattenedDeletes[0].toFrame;
  }

  flattenedDeletes = flattenedDeletes.filter(deleteData => deleteData.fromFrame >= start && deleteData.toFrame <= end);

  const filteredSpeakerOverlaps = speakerOverlaps
    .filter(overlap => overlap.toFrame > start && overlap.fromFrame < end)
    .map(overlap => ({
      ...overlap,
      fromFrame: Math.max(overlap.fromFrame, start),
      toFrame: Math.min(overlap.toFrame, end)
    }));

  const layoutChanges: LayoutChange[] = [];
  const playRanges: PlayRanges = [];
  for (const overlap of filteredSpeakerOverlaps) {
    let finalLayout = layout;
    let finalVideoIds: string[] = overlap.videoIds;
    let finalFitMode = fitMode;

    let isCreatedFromOriginalLayout = false;

    if (scenes.length > 0) {
      // Check if there's a scene that contains this overlap
      // Convert overlap frames to absolute frames by subtracting the clip's start offset
      const sceneForOverlap = scenes.find(scene =>
        isApproxWindowSubset([overlap.fromFrame, overlap.toFrame], [scene.fromFrame, scene.toFrame])
      );

      if (sceneForOverlap) {
        finalLayout = sceneForOverlap.details.layout;
        finalFitMode = sceneForOverlap.details.fitMode;

        finalVideoIds = finalVideoIds.filter(id => sceneForOverlap.details.visible_video_ids.includes(id));
      }
    }

    if (isSpeakerLayout(finalLayout) && isClipFromUploads(clip)) {
      finalFitMode = 'FILL';
    }

    if (isLayoutAudiogram(finalLayout)) {
      finalVideoIds = removeScreenshareWithoutSpeaker({ clip, videoIds: finalVideoIds });
      if (finalVideoIds.length === 0 && layoutChanges.length > 0) {
        finalVideoIds = layoutChanges[layoutChanges.length - 1].videoIds;
      } else if (finalVideoIds.length > 0) {
        finalVideoIds = [finalVideoIds[0]];
      }
    } else if (isLayoutDefault(finalLayout)) {
      finalVideoIds = [finalVideoIds[0]];
      isCreatedFromOriginalLayout = true;
      finalLayout = 'GRID';

      finalFitMode = 'FIT';
    } else {
      if (overlap.hasScreenshare && !['SPEAKER', 'PIP'].includes(finalLayout)) {
        finalLayout = 'FOCUS';
      }

      if (!overlap.hasScreenshare) {
        finalVideoIds = removeScreenshareFromVideoIds({ clip, videoIds: finalVideoIds });
      }

      if (finalLayout === 'PIP') {
        if (finalVideoIds.length >= 2) {
          // PIP needs proper ordering: first active speaker/screenshare, then second active speaker
          // The reorderSpeakersByActiveSpeaker should have already sorted them correctly
          // But ensure screenshare priority if present
          if (overlap.hasScreenshare) {
            // Find screenshare video ID using the correct identification method
            const screenshareVideoId = finalVideoIds.find(id => {
              // Check if this video ID corresponds to screenshare using the correct mapping
              return isRecordingScreenshare({ broadcastId: getClipBroadcastId(clip)!, recordingId: id });
            });

            if (screenshareVideoId) {
              // Get active speakers for this time range to ensure second stream is active speaker
              const speakerDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
              const fromFrame = overlap.fromFrame / fps;
              const toFrame = overlap.toFrame / fps;
              const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetails);

              // Get non-screenshare video IDs
              const nonScreenshareIds = finalVideoIds.filter(id => id !== screenshareVideoId);

              // Find the active speaker among non-screenshare speakers
              let activeSpeakerVideoId = nonScreenshareIds[0]; // fallback to first non-screenshare
              if (activeSpeakers.length > 0) {
                const activeSpeakerId = activeSpeakers[0].speakerId;
                const activeSpeakerVideo = nonScreenshareIds.find(
                  id =>
                    getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: id }) ===
                    activeSpeakerId
                );
                if (activeSpeakerVideo) {
                  activeSpeakerVideoId = activeSpeakerVideo;
                }
              }

              // Reorder: screenshare first, then active speaker
              finalVideoIds = [screenshareVideoId, activeSpeakerVideoId];
            }
          }

          finalVideoIds = finalVideoIds.slice(0, 2);
        } else {
          finalLayout = 'GRID';
        }
      }

      //@hiteshChoudhary, please fix ths logic to make it unified, when you do refactoring
      if (finalLayout === 'SPEAKER' || finalVideoIds.length === 1) {
        if (finalLayout === 'SPEAKER' && isCustomUploadClip({ clip }) && finalVideoIds[0] !== UPLOAD_SCREENSHARE_ID) {
          const speakerDetailsForSpeakerLayout = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
          const activeSpeakersForOverlap = getActiveSpeakersWithinTimeRange(
            overlap.fromFrame / fps,
            overlap.toFrame / fps,
            speakerDetailsForSpeakerLayout
          );
          const firstVideoId = finalVideoIds[0];
          const firstVideoSpeakerKey = firstVideoId
            ? getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: firstVideoId })
            : null;

          const activeSpeakerMatchesVideo =
            firstVideoSpeakerKey !== null && activeSpeakersForOverlap.some(s => s.speakerId === firstVideoSpeakerKey);

          if (!activeSpeakerMatchesVideo) {
            // Active speaker doesn't match the video — fall back to DEFAULT layout
            const originalVideo = videos.find(v => v.isOriginalVideo);
            finalVideoIds = originalVideo ? [originalVideo.id] : finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
            isCreatedFromOriginalLayout = true;
            finalFitMode = 'FIT';
          } else {
            finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
          }
        } else {
          finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
        }

        finalLayout = 'GRID';
      }
    }

    const deletesCreepingIntoRange = flattenedDeletes.filter(
      deleteData => deleteData.toFrame > overlap.fromFrame && deleteData.fromFrame < overlap.toFrame
    );

    let overlaps: { fromFrame: number; toFrame: number; hasScreenshare: boolean }[] = [
      {
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame,
        hasScreenshare: overlap.hasScreenshare
      }
    ];

    if (deletesCreepingIntoRange.length > 0) {
      const availableRanges = getAvailableRanges([overlap.fromFrame, overlap.toFrame], deletesCreepingIntoRange);

      overlaps = availableRanges.map(range => ({
        fromFrame: range[0],
        toFrame: range[1],
        hasScreenshare: overlap.hasScreenshare
      }));
    }
    overlaps.forEach(overlap => {
      // Get crop information from the scene if available, or from speakersAnalysis for uploads
      let videoCropPositionMap: Record<string, VideoCropPosition> | undefined;
      let videoIds: string[] = finalVideoIds;
      let layoutForOverlap: LayoutEnum = finalLayout;
      const faceCoordinatesMap: Record<string, VideoCropPosition> = {};
      const videoIsUsingManualRepositioning: Record<string, boolean> = {};
      if (scenes.length > 0) {
        const sceneForOverlap = scenes.find(scene =>
          isApproxWindowSubset([overlap.fromFrame, overlap.toFrame], [scene.fromFrame, scene.toFrame])
        );

        if (sceneForOverlap) {
          if (sceneForOverlap.details.video_crop_position_map) {
            videoCropPositionMap = sceneForOverlap.details.video_crop_position_map;
          }

          const sceneLayout = sceneForOverlap.details.layout;
          const shouldFetchCropPositions = isSpeakerLayout(sceneLayout);

          if (shouldFetchCropPositions && isCustomUploadClip({ clip })) {
            const cropPositionsResult = getCropPositionsForUploads({
              clip,
              overlap,
              fps,
              finalLayout,
              fitMode,
              hideBorders,
              finalVideoIds,
              videos,
              hasVisibleVideoIds: sceneForOverlap.details.visible_video_ids.length > 0
            });

            if (
              isScreenShareEnabledForCustomUploadClips(clip) &&
              cropPositionsResult?.videoCropPositionMap?.[UPLOAD_SCREENSHARE_ID]
            ) {
              videoIsUsingManualRepositioning[UPLOAD_SCREENSHARE_ID] = true;
            }

            if (cropPositionsResult) {
              videoIds = cropPositionsResult.videoIds;
              layoutForOverlap = cropPositionsResult.layoutForOverlap;
              Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);

              if (videoCropPositionMap) {
                Object.keys(videoCropPositionMap).forEach(videoId => {
                  videoIsUsingManualRepositioning[videoId] = true;
                });
              }
              videoCropPositionMap = { ...cropPositionsResult.videoCropPositionMap, ...videoCropPositionMap };
            }
          }
        }
      } else if (scenes.length === 0) {
        // Handle elements for crop when no scenes exist
        const shouldFetchCropPositions = isSpeakerLayout(finalLayout);

        if (shouldFetchCropPositions && isCustomUploadClip({ clip })) {
          const visibleVideoIds = clip.asset_metadata.visible_video_ids || finalVideoIds;
          const filteredVideoIds = finalVideoIds.filter(videoId => visibleVideoIds.includes(videoId));
          const cropPositionsResult = getCropPositionsForUploads({
            clip,
            overlap,
            fps,
            finalLayout,
            fitMode,
            hideBorders,
            finalVideoIds: filteredVideoIds,
            videos,
            hasVisibleVideoIds: filteredVideoIds.length > 0
          });

          if (cropPositionsResult) {
            videoIds = cropPositionsResult.videoIds;
            layoutForOverlap = cropPositionsResult.layoutForOverlap;
            Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);
            videoCropPositionMap = cropPositionsResult.videoCropPositionMap;
          }
        }
      }

      const layoutDetailsAtCurrentFrame = {
        fromFrame: getAdjustedFrame(overlap.fromFrame, flattenedDeletes, start),
        toFrame: getAdjustedFrame(overlap.toFrame, flattenedDeletes, start),
        videoIds: videoIds,
        layout: layoutForOverlap,
        size,
        fitMode: finalFitMode,
        hideBorders,
        hasScreenshare: overlap.hasScreenshare,
        mutedVideosId: getMutedVideoIds({
          clip,
          videosInLayout: finalVideoIds,
          videos,
          isCreatedFromOriginalLayout
        }),
        videoCropPositionMap
      };
      const finalConfig = getFinalConfigForLayout(layoutDetailsAtCurrentFrame, videoIds);
      const videoCropPositionStyles: Record<string, VideoCropPositionStyle> = {};
      const isCustomUploads = isCustomUploadClip({ clip });
      const preventOverlappingWithOtherCrops = isCustomUploads;
      const isFitModeForSSEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Fit_Mode_SS_In_Uploads];
      videoIds.forEach((videoId, index) => {
        const videoCropPosition = videoCropPositionMap?.[videoId];
        const isUploadAndUsingManualRepositioned = isCustomUploads && videoIsUsingManualRepositioning[videoId];
        if (!videoCropPosition || !finalConfig[index]) {
          return;
        }
        const videoAspectRatio = videos.find(v => v.id === videoId)?.aspectRatio;
        if (videoId === UPLOAD_SCREENSHARE_ID && isCustomUploads && isFitModeForSSEnabled) {
          videoCropPositionStyles[videoId] = getFitCropStyles(
            videoCropPosition,
            finalConfig[index].containerStyle as CSSProperties,
            getAspectRatio(clip.asset_metadata.size),
            videoAspectRatio
          );
          return;
        }

        const isManualRepositioned = isUploadAndUsingManualRepositioned || !isCustomUploads;
        const allOtherCropInformation = preventOverlappingWithOtherCrops
          ? {
              ...Object.keys(faceCoordinatesMap || {})
                .filter(key => key !== videoId)
                .reduce((acc, key) => {
                  acc[key] = faceCoordinatesMap?.[key];
                  return acc;
                }, {}),
              ...Object.keys(videoCropPositionMap || {})
                .filter(key => key !== videoId)
                .reduce((acc, key) => {
                  acc[key] = videoCropPositionMap?.[key];
                  return acc;
                }, {})
            }
          : undefined;

        videoCropPositionStyles[videoId] = getCropStyles(
          videoCropPosition,
          finalConfig[index].containerStyle as CSSProperties,
          getAspectRatio(clip.asset_metadata.size),
          allOtherCropInformation,
          isManualRepositioned,
          videoAspectRatio,
          faceCoordinatesMap?.[videoId]
        );
      });

      layoutChanges.push({
        ...layoutDetailsAtCurrentFrame,
        videoCropPositionStyles
      });
      playRanges.push({
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame
      });
    });
  }

  if (playRanges.length && playRanges[0].fromFrame !== 0 && start === 0) {
    playRanges.unshift({
      fromFrame: 0,
      toFrame: playRanges[0].fromFrame
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[0].fromFrame !== 0
  ) {
    // if there is no speaker in first frame add first speaker
    const firstLayout = layoutChanges[0];
    layoutChanges.unshift({
      ...firstLayout,
      fromFrame: 0,
      toFrame: firstLayout.fromFrame
    });
  }

  if (playRanges.length && playRanges[playRanges.length - 1].toFrame < end) {
    // ! - TODO: account for deletes that fall in this playRange and break it accordingly
    playRanges.push({
      fromFrame: playRanges[playRanges.length - 1].toFrame,
      toFrame: end
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[layoutChanges.length - 1].toFrame < end
  ) {
    // ! - TODO: account for deletes and shift layoutChanges (It is over-extending right now but should be harmless)
    // if there is no speaker in last frame add most recent speaker
    const lastLayout = layoutChanges[layoutChanges.length - 1];
    layoutChanges.push({ ...lastLayout, fromFrame: lastLayout.fromFrame, toFrame: end });
  }

  return { layoutChanges, playRanges };
};

export const getLayoutChangeAtCurrentFrame = (clip: Clip) => {
  const currentClipMetadata = clip.asset_metadata;
  const playerConfig = currentClipMetadata.player_config;

  if (playerConfig) {
    const currentSegment = getSegmentAtCurrentFrame(remotionPlayerStateStore.getSnapshot()?.currentTime || 0, clip);
    if (!currentSegment || currentSegment.type !== SegmentType.COMPOSITE) {
      return;
    }
    const currentSegmentFrame = remotionPlayerStateStore.getSnapshot()?.currentTime || 0 - currentSegment.fromFrame;

    const layoutChanges = currentSegment.layoutChanges;

    return layoutChanges.find(
      layoutChange => layoutChange.fromFrame <= currentSegmentFrame && layoutChange.toFrame > currentSegmentFrame
    );
  }

  return;
};

/**
 * Calculates layout changes and play ranges for a composite segment, accounting for splits, deletes, and scene configurations.
 *
 * This function processes a composite segment to determine how the layout should change over time based on:
 * - Speaker overlaps and active speakers
 * - Deleted time ranges within the segment
 * - Scene-specific layout configurations
 * - Video crop positions and manual repositioning
 * - Layout type transformations (e.g., DEFAULT to GRID, SPEAKER layouts)
 *
 * The function filters and adjusts speaker overlaps based on the segment's time bounds and deletes,
 * then determines the appropriate layout, video IDs, fit mode, and crop positions for each overlap period.
 *
 * @param clip - The clip containing the composite segment
 * @param fps - Frames per second for frame calculations
 * @param layout - Default layout type (defaults to DEFAULT_LAYOUT_TYPE)
 * @param size - Default size type (defaults to DEFAULT_SIZE_TYPE)
 * @param fitMode - Default fit mode (defaults to DEFAULT_FIT_TYPE)
 * @param hideBorders - Whether to hide borders (defaults to DEFAULT_HIDE_BORDERS)
 * @param scenes - Array of scenes that may override layout settings
 * @param videos - Array of video elements available for the layout
 * @param oldSegment - The composite segment to process
 * @returns An object containing:
 *   - `layoutChanges`: Array of layout change configurations with frame ranges, video IDs, layout types, and crop positions
 *   - `playRanges`: Array of play ranges indicating which frames should be played
 *
 * @remarks
 * - Returns empty arrays if segment metadata is not found
 * - Adjusts start frame based on full-length clips and deletes at frame 0
 * - Filters speaker overlaps to only include those within the segment's time bounds
 * - Handles special cases for audiogram layouts, PIP layouts, and screenshare detection
 * - Applies scene-specific layouts and crop positions when available
 * - Accounts for deletes that intersect with speaker overlaps, creating multiple play ranges
 * - May add padding layout changes at the start/end for default layouts and audiograms
 */
export const getLayoutChangesAndPlayRangesWithSplits = ({
  clip,
  fps,
  layout = DEFAULT_LAYOUT_TYPE,
  size = DEFAULT_SIZE_TYPE,
  fitMode = DEFAULT_FIT_TYPE,
  hideBorders = DEFAULT_HIDE_BORDERS,
  scenes,
  videos,
  oldSegment
}: Pick<LayoutChange, 'layout' | 'size' | 'fitMode' | 'hideBorders'> & {
  clip: Clip;
  fps: number;
  scenes: Scene[];
  videos: VideoElement[];
  oldSegment: CompositeSegment;
}) => {
  const segmentMetadata = oldSegment?.segment_metadata;
  if (!segmentMetadata) {
    return { layoutChanges: [], playRanges: [] };
  }
  // get exact points across the entire clip with videos within those points, including screenshare
  const speakerOverlaps = getSpeakerOverlaps({ clip, fps, scenes });
  // clips can start from non-zero offset
  let start = clip.asset_metadata.is_full_length ? 0 : segmentMetadata.start * fps;
  const end = segmentMetadata.end * fps;

  let flattenedDeletes = getFlattenedDeletes({ clipDeletes: segmentMetadata.deletes || {}, fps });
  if (flattenedDeletes.length > 0 && flattenedDeletes[0].fromFrame === 0) {
    start = flattenedDeletes[0].toFrame;
  }

  flattenedDeletes = flattenedDeletes.filter(deleteData => deleteData.fromFrame >= start && deleteData.toFrame <= end);

  const filteredSpeakerOverlaps = speakerOverlaps
    .filter(overlap => overlap.toFrame > start && overlap.fromFrame < end)
    .map(overlap => ({
      ...overlap,
      fromFrame: Math.max(overlap.fromFrame, start),
      toFrame: Math.min(overlap.toFrame, end)
    }));

  const layoutChanges: LayoutChange[] = [];
  const playRanges: PlayRanges = [];
  for (const overlap of filteredSpeakerOverlaps) {
    let finalLayout = layout;
    let finalVideoIds: string[] = overlap.videoIds;
    let finalFitMode = fitMode;

    let isCreatedFromOriginalLayout = false;

    if (scenes.length > 0) {
      // Check if there's a scene that contains this overlap
      // Convert overlap frames to absolute frames by subtracting the clip's start offset
      const sceneForOverlap = scenes.find(scene =>
        isApproxWindowSubset([overlap.fromFrame, overlap.toFrame], [scene.fromFrame, scene.toFrame])
      );

      if (sceneForOverlap) {
        finalLayout = sceneForOverlap.details.layout;
        finalFitMode = sceneForOverlap.details.fitMode;

        finalVideoIds = finalVideoIds.filter(id => sceneForOverlap.details.visible_video_ids.includes(id));
      }
    }

    if (isSpeakerLayout(finalLayout) && isClipFromUploads(clip)) {
      finalFitMode = 'FILL';
    }

    if (isLayoutAudiogram(finalLayout)) {
      finalVideoIds = removeScreenshareWithoutSpeaker({ clip, videoIds: finalVideoIds });
      if (finalVideoIds.length === 0 && layoutChanges.length > 0) {
        finalVideoIds = layoutChanges[layoutChanges.length - 1].videoIds;
      } else if (finalVideoIds.length > 0) {
        finalVideoIds = [finalVideoIds[0]];
      }
    } else if (isLayoutDefault(finalLayout)) {
      finalVideoIds = [finalVideoIds[0]];
      isCreatedFromOriginalLayout = true;
      finalLayout = 'GRID';

      finalFitMode = 'FIT';
    } else {
      if (overlap.hasScreenshare && !['SPEAKER', 'PIP'].includes(finalLayout)) {
        finalLayout = 'FOCUS';
      }

      if (!overlap.hasScreenshare) {
        finalVideoIds = removeScreenshareFromVideoIds({ clip, videoIds: finalVideoIds });
      }

      if (finalLayout === 'PIP') {
        if (finalVideoIds.length >= 2) {
          // PIP needs proper ordering: first active speaker/screenshare, then second active speaker
          // The reorderSpeakersByActiveSpeaker should have already sorted them correctly
          // But ensure screenshare priority if present
          if (overlap.hasScreenshare) {
            // Find screenshare video ID using the correct identification method
            const screenshareVideoId = finalVideoIds.find(id => {
              // Check if this video ID corresponds to screenshare using the correct mapping
              return isRecordingScreenshare({ broadcastId: getClipBroadcastId(clip)!, recordingId: id });
            });

            if (screenshareVideoId) {
              // Get active speakers for this time range to ensure second stream is active speaker
              const speakerDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
              const fromFrame = overlap.fromFrame / fps;
              const toFrame = overlap.toFrame / fps;
              const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetails);

              // Get non-screenshare video IDs
              const nonScreenshareIds = finalVideoIds.filter(id => id !== screenshareVideoId);

              // Find the active speaker among non-screenshare speakers
              let activeSpeakerVideoId = nonScreenshareIds[0]; // fallback to first non-screenshare
              if (activeSpeakers.length > 0) {
                const activeSpeakerId = activeSpeakers[0].speakerId;
                const activeSpeakerVideo = nonScreenshareIds.find(
                  id =>
                    getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: id }) ===
                    activeSpeakerId
                );
                if (activeSpeakerVideo) {
                  activeSpeakerVideoId = activeSpeakerVideo;
                }
              }

              // Reorder: screenshare first, then active speaker
              finalVideoIds = [screenshareVideoId, activeSpeakerVideoId];
            }
          }

          finalVideoIds = finalVideoIds.slice(0, 2);
        } else {
          finalLayout = 'GRID';
        }
      }

      //@hiteshChoudhary, please fix ths logic to make it unified, when you do refactoring
      if (finalLayout === 'SPEAKER' || finalVideoIds.length === 1) {
        if (finalLayout === 'SPEAKER' && isCustomUploadClip({ clip }) && finalVideoIds[0] !== UPLOAD_SCREENSHARE_ID) {
          const speakerDetailsForSpeakerLayout = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
          const activeSpeakersForOverlap = getActiveSpeakersWithinTimeRange(
            overlap.fromFrame / fps,
            overlap.toFrame / fps,
            speakerDetailsForSpeakerLayout
          );
          const firstVideoId = finalVideoIds[0];
          const firstVideoSpeakerKey = firstVideoId
            ? getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: firstVideoId })
            : null;
          const activeSpeakerMatchesVideo =
            firstVideoSpeakerKey !== null && activeSpeakersForOverlap.some(s => s.speakerId === firstVideoSpeakerKey);

          if (!activeSpeakerMatchesVideo) {
            const originalVideo = videos.find(v => v.isOriginalVideo);
            finalVideoIds = originalVideo ? [originalVideo.id] : finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
            isCreatedFromOriginalLayout = true;
            finalFitMode = 'FIT';
          } else {
            finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
          }
        } else {
          finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
        }
        finalLayout = 'GRID';
      }
    }

    const deletesCreepingIntoRange = flattenedDeletes.filter(
      deleteData => deleteData.toFrame > overlap.fromFrame && deleteData.fromFrame < overlap.toFrame
    );

    let overlaps: { fromFrame: number; toFrame: number; hasScreenshare: boolean }[] = [
      {
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame,
        hasScreenshare: overlap.hasScreenshare
      }
    ];

    if (deletesCreepingIntoRange.length > 0) {
      const availableRanges = getAvailableRanges([overlap.fromFrame, overlap.toFrame], deletesCreepingIntoRange);

      overlaps = availableRanges.map(range => ({
        fromFrame: range[0],
        toFrame: range[1],
        hasScreenshare: overlap.hasScreenshare
      }));
    }
    overlaps.forEach(overlap => {
      // Get crop information from the scene if available, or from speakersAnalysis for uploads
      let videoCropPositionMap: Record<string, VideoCropPosition> | undefined;
      let videoIds: string[] = finalVideoIds;
      let layoutForOverlap: LayoutEnum = finalLayout;
      const faceCoordinatesMap: Record<string, VideoCropPosition> = {};
      const videoIsUsingManualRepositioning: Record<string, boolean> = {};
      if (scenes.length > 0) {
        const sceneForOverlap = scenes.find(
          scene => overlap.fromFrame >= scene.fromFrame && overlap.toFrame <= scene.toFrame
        );

        if (sceneForOverlap) {
          if (sceneForOverlap.details.video_crop_position_map) {
            videoCropPositionMap = sceneForOverlap.details.video_crop_position_map;
          }

          const sceneLayout = sceneForOverlap.details.layout;
          const shouldFetchCropPositions = isSpeakerLayout(sceneLayout);

          if (shouldFetchCropPositions && isCustomUploadClip({ clip })) {
            const cropPositionsResult = getCropPositionsForUploads({
              clip,
              overlap,
              fps,
              finalLayout,
              fitMode,
              hideBorders,
              finalVideoIds,
              videos,
              hasVisibleVideoIds: sceneForOverlap.details.visible_video_ids.length > 0
            });

            if (
              isScreenShareEnabledForCustomUploadClips(clip) &&
              cropPositionsResult?.videoCropPositionMap?.[UPLOAD_SCREENSHARE_ID]
            ) {
              videoIsUsingManualRepositioning[UPLOAD_SCREENSHARE_ID] = true;
            }

            if (cropPositionsResult) {
              videoIds = cropPositionsResult.videoIds;
              layoutForOverlap = cropPositionsResult.layoutForOverlap;
              Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);

              if (videoCropPositionMap) {
                Object.keys(videoCropPositionMap).forEach(videoId => {
                  videoIsUsingManualRepositioning[videoId] = true;
                });
              }
              videoCropPositionMap = { ...cropPositionsResult.videoCropPositionMap, ...videoCropPositionMap };
            }
          }
        }
      } else if (scenes.length === 0) {
        // Handle elements for crop when no scenes exist
        const shouldFetchCropPositions = isSpeakerLayout(finalLayout);

        if (shouldFetchCropPositions && isCustomUploadClip({ clip })) {
          const visibleVideoIds = clip.asset_metadata.visible_video_ids || finalVideoIds;
          const filteredVideoIds = finalVideoIds.filter(videoId => visibleVideoIds.includes(videoId));
          const cropPositionsResult = getCropPositionsForUploads({
            clip,
            overlap,
            fps,
            finalLayout,
            fitMode,
            hideBorders,
            finalVideoIds: filteredVideoIds,
            videos,
            hasVisibleVideoIds: filteredVideoIds.length > 0
          });

          if (cropPositionsResult) {
            videoIds = cropPositionsResult.videoIds;
            layoutForOverlap = cropPositionsResult.layoutForOverlap;
            Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);
            videoCropPositionMap = cropPositionsResult.videoCropPositionMap;
          }
        }
      }

      const layoutDetailsAtCurrentFrame = {
        fromFrame: getAdjustedFrame(overlap.fromFrame, flattenedDeletes, start),
        toFrame: getAdjustedFrame(overlap.toFrame, flattenedDeletes, start),
        videoIds: videoIds,
        layout: layoutForOverlap,
        size,
        fitMode: finalFitMode,
        hideBorders,
        hasScreenshare: overlap.hasScreenshare,
        mutedVideosId: getMutedVideoIds({
          clip,
          videosInLayout: finalVideoIds,
          videos,
          isCreatedFromOriginalLayout
        }),
        videoCropPositionMap
      };
      const finalConfig = getFinalConfigForLayout(layoutDetailsAtCurrentFrame, videoIds);
      const videoCropPositionStyles: Record<string, VideoCropPositionStyle> = {};
      const isCustomUploads = isCustomUploadClip({ clip });
      const preventOverlappingWithOtherCrops = isCustomUploads;
      const isFitModeForSSEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Fit_Mode_SS_In_Uploads];
      videoIds.forEach((videoId, index) => {
        const videoCropPosition = videoCropPositionMap?.[videoId];
        const isUploadAndUsingManualRepositioned = isCustomUploads && videoIsUsingManualRepositioning[videoId];
        if (!videoCropPosition || !finalConfig[index]) {
          return;
        }
        const videoAspectRatio = videos.find(v => v.id === videoId)?.aspectRatio;
        if (videoId === UPLOAD_SCREENSHARE_ID && isCustomUploads && isFitModeForSSEnabled) {
          videoCropPositionStyles[videoId] = getFitCropStyles(
            videoCropPosition,
            finalConfig[index].containerStyle as CSSProperties,
            getAspectRatio(clip.asset_metadata.size),
            videoAspectRatio
          );
          return;
        }

        const isManualRepositioned = isUploadAndUsingManualRepositioned || !isCustomUploads;
        const allOtherCropInformation = preventOverlappingWithOtherCrops
          ? {
              ...Object.keys(faceCoordinatesMap || {})
                .filter(key => key !== videoId)
                .reduce((acc, key) => {
                  acc[key] = faceCoordinatesMap?.[key];
                  return acc;
                }, {}),
              ...Object.keys(videoCropPositionMap || {})
                .filter(key => key !== videoId)
                .reduce((acc, key) => {
                  acc[key] = videoCropPositionMap?.[key];
                  return acc;
                }, {})
            }
          : undefined;

        videoCropPositionStyles[videoId] = getCropStyles(
          videoCropPosition,
          finalConfig[index].containerStyle as CSSProperties,
          getAspectRatio(clip.asset_metadata.size),
          allOtherCropInformation,
          isManualRepositioned,
          videoAspectRatio,
          faceCoordinatesMap?.[videoId]
        );
      });

      layoutChanges.push({
        ...layoutDetailsAtCurrentFrame,
        videoCropPositionStyles
      });
      playRanges.push({
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame
      });
    });
  }

  if (playRanges.length && playRanges[0].fromFrame !== 0 && start === 0) {
    playRanges.unshift({
      fromFrame: 0,
      toFrame: playRanges[0].fromFrame
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[0].fromFrame !== 0
  ) {
    // if there is no speaker in first frame add first speaker
    const firstLayout = layoutChanges[0];
    layoutChanges.unshift({
      ...firstLayout,
      fromFrame: 0,
      toFrame: firstLayout.fromFrame
    });
  }

  if (playRanges.length && playRanges[playRanges.length - 1].toFrame < end) {
    // ! - TODO: account for deletes that fall in this playRange and break it accordingly
    playRanges.push({
      fromFrame: playRanges[playRanges.length - 1].toFrame,
      toFrame: end
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[layoutChanges.length - 1].toFrame < end
  ) {
    // ! - TODO: account for deletes and shift layoutChanges (It is over-extending right now but should be harmless)
    // if there is no speaker in last frame add most recent speaker
    const lastLayout = layoutChanges[layoutChanges.length - 1];
    layoutChanges.push({ ...lastLayout, fromFrame: lastLayout.fromFrame, toFrame: end });
  }

  return { layoutChanges, playRanges };
};
